<script setup>
import { computed } from 'vue';
import { getInboxIconByType, getInboxBrandColor } from 'dashboard/helper/inbox';
import { useRouter, useRoute } from 'vue-router';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper.js';
import { dynamicTime, shortTimestamp } from 'shared/helpers/timeHelper';

import Icon from 'dashboard/components-next/icon/Icon.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import CardMessagePreview from './CardMessagePreview.vue';
import CardMessagePreviewWithMeta from './CardMessagePreviewWithMeta.vue';
import CardPriorityIcon from './CardPriorityIcon.vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  contact: {
    type: Object,
    required: true,
  },
  stateInbox: {
    type: Object,
    required: true,
  },
  accountLabels: {
    type: Array,
    required: true,
  },
});

const router = useRouter();
const route = useRoute();

const currentContact = computed(() => props.contact);

const currentContactName = computed(() => currentContact.value?.name);
const currentContactThumbnail = computed(() => currentContact.value?.thumbnail);
const currentContactStatus = computed(
  () => currentContact.value?.availabilityStatus
);

const inbox = computed(() => props.stateInbox);

const inboxName = computed(() => inbox.value?.name);

const inboxIcon = computed(() => {
  const { phoneNumber, channelType } = inbox.value;
  return getInboxIconByType(channelType, phoneNumber);
});

const inboxBrandColor = computed(() => {
  const { phoneNumber, channelType } = inbox.value;
  return getInboxBrandColor(channelType, phoneNumber);
});

const shouldShowInboxBadge = computed(() => {
  const { channelType } = inbox.value;
  // Hiển thị badge cho Zalo và các inbox khác có thể cần phân biệt
  return (
    channelType === 'Channel::ZaloPersonal' ||
    channelType === 'Channel::ZaloOa' ||
    channelType === 'Channel::Line' ||
    channelType === 'Channel::Telegram'
  );
});

const hasUnread = computed(() => {
  return props.conversation?.unreadCount > 0;
});

const lastActivityAt = computed(() => {
  const timestamp = props.conversation?.timestamp;
  return timestamp ? shortTimestamp(dynamicTime(timestamp)) : '';
});

const showMessagePreviewWithoutMeta = computed(() => {
  const { slaPolicyId, labels = [] } = props.conversation;
  return !slaPolicyId && labels.length === 0;
});

const onCardClick = e => {
  const path = frontendURL(
    conversationUrl({
      accountId: route.params.accountId,
      id: props.conversation.id,
    })
  );

  if (e.metaKey || e.ctrlKey) {
    window.open(
      window.hichatConfig.hostURL + path,
      '_blank',
      'noopener noreferrer nofollow'
    );
    return;
  }
  router.push({ path });
};
</script>

<template>
  <div
    class="flex w-full gap-3 px-3 py-4 transition-all duration-300 ease-in-out cursor-pointer"
    :class="{
      'bg-slate-50 dark:bg-slate-800': hasUnread,
      'hover:bg-slate-50 dark:hover:bg-slate-800': !hasUnread,
    }"
    @click="onCardClick"
  >
    <Avatar
      :name="currentContactName"
      :src="currentContactThumbnail"
      :size="24"
      :status="currentContactStatus"
      rounded-full
    >
      <template #badge="{ size }">
        <div
          v-if="shouldShowInboxBadge"
          class="absolute z-20 flex items-center justify-center rounded-full bg-n-slate-3 dark:bg-n-slate-4 border border-n-slate-3 dark:border-n-slate-4"
          :style="{
            width: `${(size * 0.4)}px`,
            height: `${(size * 0.4)}px`,
            top: `${size - (size * 0.4) / 1.1}px`,
            left: `${size - (size * 0.4) / 1.1}px`,
          }"
        >
          <Icon
            :icon="inboxIcon"
            :style="{
              color: inboxBrandColor,
              width: `${(size * 0.25)}px`,
              height: `${(size * 0.25)}px`,
            }"
          />
        </div>
      </template>
    </Avatar>
    <div class="flex flex-col w-full gap-1">
      <div class="flex items-center justify-between h-6 gap-2">
        <h4 
          class="text-base font-medium truncate"
          :class="{
            'text-n-slate-12 font-semibold': hasUnread,
            'text-n-slate-12': !hasUnread,
          }"
        >
          {{ currentContactName }}
        </h4>
        <div class="flex items-center gap-2">
          <CardPriorityIcon :priority="conversation.priority || null" />
          <div
            v-tooltip.left="inboxName"
            class="flex items-center justify-center flex-shrink-0 rounded-full bg-n-alpha-2 size-5"
          >
            <Icon
              :icon="inboxIcon"
              class="flex-shrink-0 text-n-slate-11 size-3"
            />
          </div>
          <span class="text-sm text-n-slate-10">
            {{ lastActivityAt }}
          </span>
        </div>
      </div>
      <CardMessagePreview
        v-if="showMessagePreviewWithoutMeta"
        :conversation="conversation"
      />
      <CardMessagePreviewWithMeta
        v-else
        :conversation="conversation"
        :account-labels="accountLabels"
      />
    </div>
  </div>
</template>
